import CacheBase2 from '../../services/engines/CacheBase2';
import { userSettingsDefs } from './userSettingsDefs';
import getRegionDefaults from './RegionDefaults';

class UserSettingsCache extends CacheBase2 {
    _name = 'UserSettingsCache';

    constructor() {
        super();
        // Fill with defaults immediately on construction — consumers may read resolveAll()
        // before PageContent.componentDidMount runs bootup() (e.g. UserSettingsProvider's
        // initial useState). bootup()'s initDefaults() call becomes a safe no-op re-fill.
        this.initDefaults();
    }

    /**
     * Fills cache with defaults from userSettingsDefs — called once at bootup.
     * Keys with default: null (dateFormat, chipDateTimeFormat) fall back to locale-based region defaults.
     */
    initDefaults = () => {
        this._items = userSettingsDefs.map((d) => ({
            key: d.key,
            value: d.default ?? getRegionDefaults()[d.key.split('.').pop()] ?? null,
        }));
    };

    /**
     * Returns the resolved (already-typed) value for a key, or null if not present.
     * @param {string} key
     * @returns {*}
     */
    getValue = (key) => {
        const item = this._items.find((i) => i.key === key);
        return item?.value ?? null;
    };

    /**
     * Returns all settings as a flat key/value object.
     * @returns {object}
     */
    getAllValues = () => {
        return Object.fromEntries(this._items.map((i) => [i.key, i.value]));
    };

    /**
     * Upserts entries by key — coerces value to the def's type (bool/number) on the way in.
     * CacheBase2's own addOrUpdate assumes rk, settings use key.
     * @param {{key:string, value:*}[]} items
     */
    upsertByKey = (items) => {
        items.forEach((item) => {
            const def = userSettingsDefs.find((d) => d.key === item.key);

            let value = item.value;
            if (def?.type === 'bool') value = value === 'true' || value === true;
            else if (def?.type === 'number') value = Number(value);

            const existing = this._items.find((i) => i.key === item.key);
            if (existing) existing.value = value;
            else this._items.push({ key: item.key, value });
        });
    };
}

const userSettingsCache = new UserSettingsCache();
export default userSettingsCache;
